import { useIntl } from 'react-intl'

import BaseCompactCard from './BaseCompactCard'

import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { ItemMetadata } from 'tripPlanner/models'
import type { CarRentalItem } from 'tripPlanner/types/tripItem'
import { getDayOfText } from 'tripPlanner/utils'
import { isSamePlace } from 'tripPlanner/utils/places'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'

interface Props {
  item: CarRentalItem
  presentationType: 'start' | 'end' | 'intermediary'
  date?: moment.Moment
  variant: DetailItemCardVariant
}

function CompactTransportCard({ item, presentationType, date, variant }: Props) {
  const { type, startPlace, endPlace, startTime, endTime } = item
  const { icon: Icon } = ItemMetadata[type]

  const timeFormat = useRegionTimeFormat()
  const intl = useIntl()

  let title: string
  let description: string | undefined

  const isMultiDay =
    item.endDate && item.endDate.diff(item.startDate, 'days') > 0

  if (presentationType === 'start') {
    if (isMultiDay || !endPlace) {
      title = intl.formatMessage(
        {
          id: 'rItsbW7NrP0Izn4pBC2A',
          defaultMessage: 'Pick up at {placeName}',
          description: 'Car rental pick up location title for multi-day or one-way rental',
        },
        { placeName: startPlace.name },
      )
    } else if (isSamePlace(startPlace, endPlace)) {
      title = intl.formatMessage(
        {
          id: 'vz__e5-ieexbM9bv6WBG',
          defaultMessage: 'Pick up and drop off at {placeName}',
          description: 'Car rental title when pick up and drop off are at the same location',
        },
        { placeName: startPlace.name },
      )
    } else {
      title = intl.formatMessage(
        {
          id: 'sQsogpzvpAzizoFZOidp',
          defaultMessage: 'Pick up at {pickUpPlace}, drop off at {dropOffPlace}',
          description: 'Car rental title showing different pick up and drop off locations',
        },
        { pickUpPlace: startPlace.name, dropOffPlace: endPlace.name },
      )
    }
    if (startTime) {
      description = `Pick up at ${startTime.format(timeFormat)}`
    }
  } else if (presentationType === 'intermediary') {
    title = intl.formatMessage({
      id: 'UZfB0dTQ21SDsbcoTAjF',
      defaultMessage: 'Car rental',
      description: 'Title for a car rental item shown on intermediary days in the itinerary',
    })
    if (date) {
      description = getDayOfText(item, date)
    }
  } else {
    if (endPlace?.name) {
      title = intl.formatMessage(
        {
          id: 'K7YTJifEUvFiapRzmUSH',
          defaultMessage: 'Drop off at {placeName}',
          description: 'Heading title for car rental drop off with location name',
        },
        { placeName: endPlace.name },
      )
    } else {
      title = intl.formatMessage({
        id: 'Eifn_NJfyhfRUGUReRc8',
        defaultMessage: 'Drop off',
        description: 'Heading title for car rental drop off without location',
      })
    }
    if (endTime) {
      description = `Drop off at ${endTime.format(timeFormat)}`
    }
  }

  return (
    <BaseCompactCard
      item={item}
      icon={<Icon size="S" />}
      title={title}
      description={description}
      isPrimary={presentationType === 'start'}
      variant={variant}
    />
  )
}

export default CompactTransportCard
